import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Platform, Alert, Linking } from 'react-native';
import * as Notifications from 'expo-notifications';
import Constants from 'expo-constants';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { navigationRef } from '../navigation/navigationRef';
import { getData, postData, putData } from '../services/api';

// Configure notification behavior
Notifications.setNotificationHandler({
    handleNotification: async () => ({
        shouldShowAlert: true,
        shouldPlaySound: true,
        shouldSetBadge: true,
    }),
});

const NotificationContext = createContext({
    notifications: [],
    unreadCount: 0,
    pushToken: null,
    fetchNotifications: () => { },
    markAsRead: () => { },
    markAllAsRead: () => { },
    showLocalNotification: () => { },
    handleNotificationPress: () => { },
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const { socket } = useSocket();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pushToken, setPushToken] = useState(null);
    const notificationListener = useRef();
    const responseListener = useRef();



    useEffect(() => {
        if (isAuthenticated) {
            registerForPushNotificationsAsync().then(token => {
                if (token) {
                    setPushToken(token);
                    updatePushTokenOnBackend(token);
                }
            });
            fetchNotifications();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        // Socket listener for real-time notifications
        if (socket && user) {
            const eventName = `notification_${user.id}`;
            socket.on(eventName, (newNotif) => {
                setNotifications(prev => [newNotif, ...prev]);
                setUnreadCount(prev => prev + 1);

                // Show in-app toast for socket notifications if app is open
                // Include .type inside the .data object matching remote Expo push notifications
                showLocalNotification(newNotif.title, newNotif.message, { ...newNotif.data, type: newNotif.type });
            });
            return () => socket.off(eventName);
        }
    }, [socket, user?.id]);

    useEffect(() => {
        // This listener is fired whenever a notification is received while the app is foregrounded
        notificationListener.current = Notifications.addNotificationReceivedListener(notification => {
            console.log('Push notification received in foreground:', notification);
            // We already handle socket notifications for real-time UI, 
            // but push notifications also trigger this if received while open.
        });

        // This listener is fired whenever a user taps on or interacts with a notification
        responseListener.current = Notifications.addNotificationResponseReceivedListener(response => {
            const data = response.notification.request.content.data;
            console.log('Notification tapped from background:', data);
            
            // Handle notification press immediately
            if (data) {
                handleNotificationPress({ type: data.type, data }, null);
            }
        });

        return () => {
            Notifications.removeNotificationSubscription(notificationListener.current);
            Notifications.removeNotificationSubscription(responseListener.current);
        };
    }, []);

    const fetchNotifications = async () => {
        try {
            const data = await getData('/notifications');
            setNotifications(data.notifications || []);
            setUnreadCount(data.unreadCount || 0);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const markAsRead = async (id) => {
        try {
            await putData(`/notifications/${id}/read`);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await putData('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const updatePushTokenOnBackend = async (token) => {
        try {
            await postData('/notifications/push-token', { token });
        } catch (error) {
            console.error('Error updating push token on backend:', error);
        }
    };

    const registerForPushNotificationsAsync = async () => {
        let token;
        if (Platform.OS === 'android') {
            await Notifications.setNotificationChannelAsync('default', {
                name: 'default',
                importance: Notifications.AndroidImportance.MAX,
                vibrationPattern: [0, 250, 250, 250],
                lightColor: '#FF231F7C',
            });
        }

        if (Constants.isDevice) {
            const { status: existingStatus } = await Notifications.getPermissionsAsync();
            let finalStatus = existingStatus;
            if (existingStatus !== 'granted') {
                const { status } = await Notifications.requestPermissionsAsync();
                finalStatus = status;
            }
            if (finalStatus !== 'granted') {
                console.warn('Failed to get push token for push notification!');
                return;
            }
            token = (await Notifications.getExpoPushTokenAsync({
                projectId: Constants.expoConfig.extra.eas.projectId,
            })).data;
        } else {
            console.log('Must use physical device for Push Notifications');
        }

        return token;
    };

    const showLocalNotification = async (title, body, data = {}) => {
        await Notifications.scheduleNotificationAsync({
            content: {
                title,
                body,
                data,
            },
            trigger: null, // show immediately
        });
    };

    const handleNotificationPress = (notification, navigation) => {
        if (!notification) return;
        const nav = navigation || (navigationRef.isReady() ? navigationRef : null);
        if (!nav) {
            console.warn('Navigation not ready for notification press');
            return;
        }

        const { type, data } = notification;
        console.log(`Handling notification press: ${type}`, data);

        try {
            switch (type) {
                case 'post_like':
                case 'post_comment':
                    if (data?.postId) {
                        nav.navigate('Connect', {
                            screen: 'PostDetail',
                            params: { postId: data.postId }
                        });
                    }
                    break;

                case 'new_follower':
                    if (data?.followerId) {
                        nav.navigate('Connect', {
                            screen: 'UserProfile',
                            params: { userId: data.followerId }
                        });
                    }
                    break;

                case 'new_message':
                    if (data?.roomId) {
                        nav.navigate('Connect', {
                            screen: 'ChatRoom',
                            params: { roomId: data.roomId }
                        });
                    }
                    break;

                case 'order_update':
                case 'new_order':
                    if (data?.orderId) {
                        nav.navigate('OrderDetail', { orderId: data.orderId });
                    } else {
                        nav.navigate('OrderHistory');
                    }
                    break;

                case 'savings_update':
                case 'challenge_update':
                    nav.navigate('Savings');
                    break;

                case 'loan_update':
                    nav.navigate('Loans');
                    break;

                case 'live_space':
                case 'mentor_space':
                    nav.navigate('MentorSpaces', { 
                        spaceId: data?.spaceId || data?.sessionId 
                    });
                    break;

                default:
                    console.log('No specific navigation for notification type:', type);
                    break;
            }
        } catch (error) {
            console.error('Navigation error from notification:', error);
        }
    };

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            pushToken,
            fetchNotifications,
            markAsRead,
            markAllAsRead,
            showLocalNotification,
            handleNotificationPress,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};
